import { LogUserActivity } from '@/apis/users/requests'
import { useAppAlert } from '@/app/AppAlertProvider'
import FamilyWizardLayout from '@/app/pages/family_application/FamilyWizardLayout.tsx'
import { SubmitButton } from '@/app/pages/family_application/student_details/shared/submit-button.tsx'
import { YYYYMMDDToJsDate } from '@/app/utils/date'
import { DatePicker } from '@/common/components/DatePicker.tsx'
import { Input } from '@/common/components/Input.tsx'
import { AppRoutePaths } from '@/routing/route_paths.ts'
import { UpdateApplicant } from '@/storage/applications/familyApplicationSlice.ts'
import { selectProgramID } from '@/storage/program_settings/programSettingsSlice'
import { AppDispatch, RootState } from '@/storage/store'
import { zodResolver } from '@hookform/resolvers/zod'
import { format, Locale } from 'date-fns'
import { enUS, es } from 'date-fns/locale'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { LOG_TYPES } from '../../constants'

const studentSchema = z.object({
  first_name: z
    .string()
    .min(1, { message: 'first_name_is_required' })
    .regex(/^(?! )(?!.* {2})[a-zA-ZñÑãÃõÕéÉáÁíÍóÓúÚ\s,.'-]+$/, {
      message: 'invalid_first_name',
    }),
  middle_name: z
    .string()
    .regex(/^(?! )(?!.* {2})[a-zA-ZñÑãÃõÕéÉáÁíÍóÓúÚ\s,.'-]+$/, {
      message: 'invalid_middle_name',
    })
    .optional()
    .or(z.literal('')),
  last_name: z
    .string()
    .min(1, { message: 'last_name_is_required' })
    .regex(/^(?! )(?!.* {2})[a-zA-ZñÑãÃõÕéÉáÁíÍóÓúÚ\s,.'-]+$/, {
      message: 'invalid_last_name',
    }),
  date_of_birth: z.date({
    required_error: 'birth_date_is_required',
  })
})
type FormValues = z.infer<typeof studentSchema>

function getLocale(string: string): Locale {
  if (string === 'es') {
    return es
  }
  return enUS
}

const IdentityManualInfo = () => {
  const [open, setIsOpen] = useState<boolean>(false)
  const { t: translate, i18n } = useTranslation()
  const { fireAlert } = useAppAlert()
  const currentLanguage = i18n.language

  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const programId = useSelector(selectProgramID)

  const FamilyApplicationState = useSelector(
    (state: RootState) => state.FamilyApplication
  )

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(studentSchema),
    defaultValues: {
      first_name: FamilyApplicationState?.data?.first_name ?? '',
      last_name: FamilyApplicationState?.data?.last_name ?? '',
      middle_name: FamilyApplicationState?.data?.middle_name ?? '',
      date_of_birth: FamilyApplicationState?.data?.date_of_birth
        ? YYYYMMDDToJsDate(FamilyApplicationState.data.date_of_birth)
        : undefined
    },
  })

  const hasLoggedPageView = useRef(false);
  useEffect(() => {
    if (!hasLoggedPageView.current) {
      LogUserActivity({
        log_type: LOG_TYPES.VIEW_PAGE,
        suffix_text: 'manual Identity verification page',
      })
      hasLoggedPageView.current = true;
    }
  }, []) 

  const onSubmit = async (data: FormValues) => {
    const result = await dispatch(
      UpdateApplicant({
        program_id: programId,
        first_name: data.first_name,
        middle_name: data.middle_name,
        last_name: data.last_name,
        date_of_birth: format(data.date_of_birth, 'yyyy-MM-dd'),
      })
    )
    if (!UpdateApplicant.fulfilled.match(result)) {
      await fireAlert({
        variant: 'error',
        message: 'error',
      })
      return
    }
    navigate(
      AppRoutePaths.family_application.identity_verification.document_capture
    )
  }

  return (
    <FamilyWizardLayout>
      <FamilyWizardLayout.Header>
        <h1>{translate('identity_verification')}</h1>
      </FamilyWizardLayout.Header>
      <FamilyWizardLayout.Content className="w-full">
        <form id="student-details-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="flex flex-col mt-6 space-y-2">
            <label
              htmlFor="first_name"
              className="block text-sm font-bold leading-6 text-gray-900"
            >
              {translate('identity_fields.legal_first_name')}{' '}
              <span className="text-danger ml-1">*</span>
            </label>
            <div>
              <Input
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1
                ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                {...register('first_name')}
              />
            </div>
            {errors.first_name && (
              <p className="mt-1 text-sm text-red-600">
                {translate(errors.first_name.message ?? '')}
              </p>
            )}
          </div>
          <div className="flex flex-col mt-6 space-y-2">
            <label
              htmlFor="middle_name"
              className="block text-sm font-bold leading-6 text-gray-900"
            >
              {translate('identity_fields.legal_middle_name')}
              {''}
            </label>
            <div>
              <Input
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1
                ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                {...register('middle_name')}
              />
            </div>
            {errors.middle_name && (
              <p className="mt-1 text-sm text-red-600">
                {translate(errors.middle_name.message ?? '')}
              </p>
            )}
          </div>

          <div className="flex flex-col mt-6 space-y-2">
            <label
              htmlFor="last_name"
              className="block text-sm font-bold leading-6 text-gray-900"
            >
              {translate('identity_fields.legal_last_name')}{' '}
              <span className="text-danger ml-1">*</span>
            </label>
            <div>
              <Input
                className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                {...register('last_name')}
              />
            </div>
            {errors.last_name && (
              <p className="mt-1 text-sm text-red-600">
                {translate(errors.last_name.message ?? '')}
              </p>
            )}
          </div>

          <div className="flex flex-col mt-6 space-y-2">
            <label className="block text-sm font-bold text-gray-900">
              {translate('birth_date')}
              <span className="text-danger ml-1">*</span>
            </label>
            <Controller
              name="date_of_birth"
              control={control}
              render={({ field }) => (
                <DatePicker
                  fromDate={new Date('1900-01-01')}
                  toDate={new Date()}
                  captionLayout="dropdown"
                  open={open}
                  onOpenChange={setIsOpen}
                  locale={getLocale(currentLanguage)}
                  className="w-full"
                  selected={field.value}
                  defaultMonth={field.value}
                  mode="single"
                  onSelect={(date) => {
                    field.onChange(date)
                    if (date) {
                      setIsOpen(false)
                    }
                  }}
                />
              )}
            />
            {errors.date_of_birth && (
              <p className="text-sm text-red-600">
                {translate(errors.date_of_birth.message as string)}
              </p>
            )}
          </div>
        </form>
      </FamilyWizardLayout.Content>
      <FamilyWizardLayout.ActionBar>
        {' '}
        <SubmitButton
          form="student-details-form"
          status={'success'}
          buttonTxt={translate('upload_identity_doc')}
        />
      </FamilyWizardLayout.ActionBar>
    </FamilyWizardLayout>
  )
}
export default IdentityManualInfo
